import React, { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { EventCard } from "@/components/EventCard";
import { EventFilters, EMPTY_FILTERS, Filters, filterParams } from "@/components/EventFilters";
import { GOVERNORATES } from "@/pages/PartnerRegister";
import { api } from "@/lib/api";
import { todayInBaghdad } from "@/lib/time";
import { DateField } from "@/components/DateField";
import { usePrefs } from "@/contexts/PrefsContext";
import type { AppEvent, EventType } from "@/types";

const pill = (on: boolean) => `h-10 px-4 shrink-0 rounded-full text-sm font-bold border transition-[background-color,color] ${on ? "bg-foreground text-background border-foreground" : "bg-card hover:bg-accent"}`;

export const Explore = () => {
  const { t, lang } = usePrefs();
  const [types, setTypes] = useState<EventType[]>([]);
  const [usedTypes, setUsedTypes] = useState<string[]>([]);
  const [type, setType] = useState("all");
  const [gov, setGov] = useState("");
  const [date, setDate] = useState("");
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const [events, setEvents] = useState<AppEvent[] | null>(null);
  const [f, setF] = useState<Filters>(EMPTY_FILTERS);
  const params = JSON.stringify({ type, q: q || undefined, governorate: gov || undefined, date: date || undefined, ...filterParams(f) });

  useEffect(() => {
    api.get("/event-types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/events").then((r) => setUsedTypes(Array.from(new Set(r.data.map((e: AppEvent) => e.type))))).catch(() => {});
  }, []);
  useEffect(() => {
    const id = setTimeout(() => setQ(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);
  useEffect(() => {
    setEvents(null);
    const id = setTimeout(() => api.get("/events", { params: JSON.parse(params) }).then((r) => setEvents(r.data)).catch(() => setEvents([])), 250);
    return () => clearTimeout(id);
  }, [params]);

  const shownTypes = types.filter((x) => usedTypes.includes(x.slug));
  const selType = types.find((x) => x.slug === type);
  const pickType = (slug: string) => {
    setType(slug);
    setF({ ...f, audience: "", category: "", program_type: "" });
  };
  const dirty = type !== "all" || gov || date || q || JSON.stringify(f) !== JSON.stringify(EMPTY_FILTERS);
  const clearAll = () => {
    setType("all"); setGov(""); setDate(""); setQuery(""); setF(EMPTY_FILTERS);
  };

  return (
    <section id="explore" className="space-y-5" data-testid="explore-section">
      <h2 className="font-display text-xl sm:text-2xl font-black">{t("ev.title")}</h2>
      <div className="relative">
        <Search className="absolute top-1/2 -translate-y-1/2 start-4 h-5 w-5 text-muted-foreground pointer-events-none" />
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search.placeholder")} data-testid="explore-search-input"
          className="w-full h-14 rounded-full border-2 bg-card ps-12 pe-12 text-base shadow-sm focus:outline-none focus:border-primary transition-[border-color] [&::-webkit-search-cancel-button]:hidden" />
        {query && (
          <button data-testid="explore-search-clear" onClick={() => setQuery("")} aria-label={t("search.clear")} className="absolute top-1/2 -translate-y-1/2 end-3 h-9 w-9 grid place-items-center rounded-full bg-accent"><X className="h-4 w-4" /></button>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="type-pills">
        <button className={pill(type === "all")} onClick={() => pickType("all")} data-testid="type-pill-all">{t("type.all")}</button>
        {shownTypes.map((x) => (
          <button key={x.slug} className={pill(type === x.slug)} onClick={() => pickType(x.slug)} data-testid={`type-pill-${x.slug}`}>{lang === "ar" ? x.name_ar : x.name_en}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={gov} onChange={(e) => { setGov(e.target.value); setF({ ...f, area: "" }); }} data-testid="filter-governorate" className="h-10 rounded-full border bg-card px-3 text-sm font-semibold">
          <option value="">{t("ev.allGov")}</option>
          {GOVERNORATES.map((g) => <option key={g} value={g}>{t(`gov.${g}`)}</option>)}
        </select>
        <button className={pill(date === todayInBaghdad())} onClick={() => setDate(date === todayInBaghdad() ? "" : todayInBaghdad())} data-testid="filter-today">{t("ev.today")}</button>
        <DateField value={date} min={todayInBaghdad()} onChange={setDate} testId="filter-date" className="w-auto" />
        {date && <button className="h-10 px-3 text-sm font-bold text-muted-foreground" onClick={() => setDate("")} data-testid="filter-date-clear">{t("ev.anyDate")}</button>}
      </div>
      <EventFilters f={f} setF={setF} gov={gov} type={selType} />
      {events !== null && (
        <div className="flex items-center justify-between text-sm">
          <span data-testid="explore-count" className="font-bold text-muted-foreground">{events.length === 1 ? t("b4.countOne") : t("b4.count").replace("{n}", String(events.length))}</span>
          {dirty && <button data-testid="explore-clear-filters" onClick={clearAll} className="font-bold text-primary">{t("b4.clear")}</button>}
        </div>
      )}
      {events === null ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="aspect-[4/5] rounded-[1.75rem] bg-muted animate-pulse" />)}</div>
      ) : events.length === 0 ? (
        <p data-testid="explore-empty" className="text-center text-muted-foreground py-16">{t("ev.empty")}</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{events.map((e, i) => <EventCard key={e.event_id} e={e} index={i} />)}</div>
      )}
    </section>
  );
};
